import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';
import { navigate } from 'react-router-dom';

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await api.post('/auth/login', credentials);
    localStorage.setItem('accessToken', response.data.data.accessToken);
    localStorage.setItem('refreshToken', response.data.data.refreshToken);
    localStorage.setItem('tenants', JSON.stringify(response.data.data.tenants || []));
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message || 'Login failed. Check if backend is running.' });
  }
});

export const register = createAsyncThunk('auth/register', async (userData, { rejectWithValue }) => {
  try {
    const response = await api.post('/auth/register', userData);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const getCurrentUser = createAsyncThunk('auth/getCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/auth/me');
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const selectTenant = createAsyncThunk('auth/selectTenant', async (tenantId, { rejectWithValue }) => {
  try {
    const response = await api.post('/auth/select-tenant', { tenantId });
    localStorage.setItem('tenantSession', JSON.stringify(response.data.data));
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const refreshToken = createAsyncThunk('auth/refreshToken', async (_, { getState, rejectWithValue }) => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return rejectWithValue({ message: 'No refresh token' });
    }
    const response = await api.post('/auth/refresh', { refreshToken });
    localStorage.setItem('accessToken', response.data.data.accessToken);
    localStorage.setItem('refreshToken', response.data.data.refreshToken);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    isAuthenticated: !!localStorage.getItem('accessToken'),
    tenants: JSON.parse(localStorage.getItem('tenants') || '[]'),
    currentTenant: JSON.parse(localStorage.getItem('tenantSession') || 'null'),
    isLoading: false,
    error: null
  },
  reducers: {
    logout: (state) => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tenants');
      localStorage.removeItem('tenantSession');
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.tenants = [];
      state.currentTenant = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setAuth: (state, action) => {
      const { token, refreshToken } = action.payload;
      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      state.accessToken = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.user = action.payload.user;
        state.tenants = action.payload.tenants || [];
        state.requiresTenantSelection = action.payload.requiresTenantSelection;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Login failed';
      })
      .addCase(register.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user || action.payload;
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.isLoading = false;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tenants');
        localStorage.removeItem('tenantSession');
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.tenants = [];
        state.currentTenant = null;
      })
      .addCase(selectTenant.fulfilled, (state, action) => {
        state.currentTenant = action.payload;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
      });
  }
});

export const { logout, clearError, setAuth, setUser } = authSlice.actions;
export default authSlice.reducer;